import { DateLike, Nullable } from 'utils'
import {
  AliasTypeAndValue,
  ClearingHouse,
  ClientLogConfig,
  Collateral,
  CollateralAllocation,
  CollateralBasket,
  CollateralBasketType,
  CollateralPrice,
  Collection,
  CrudEventEnum,
  Currency,
  Deal,
  DealEvent,
  Direction,
  EntityWrapper,
  FatFingerProductSectorLimit,
  FatFingerUserLimit,
  FundTradingEntityAssignment,
  FxRate,
  HaircutEntry,
  Holiday,
  Index,
  IssuerOrganisation,
  LogSeverity,
  Negotiation,
  NegotiationEvent,
  NegotiationFieldMeta,
  NegotiationHistory,
  NegotiationStatus,
  NestedLegs,
  Notes,
  Order,
  OrderEvent,
  OrderTerm,
  Organisation,
  OrganisationMeta,
  OrganisationType,
  Platform,
  PrimeBroker,
  Product,
  ProductSector,
  ProductSectorMappingDetail,
  ProductSectorMappingDraftDetail,
  ReferenceDataManagementEvent,
  ReferenceRate,
  RepoType,
  ServicingSideDetails,
  SettlementVenue,
  Sponsor,
  TradeConditionFlag,
  TradingControlLimit,
  TradingControlLimitUserUtilisationCurrency,
  TradingEntity,
  TradingEntityClearing,
  TradingEntityMeta,
  TradingRelationship,
  User,
  ValidationIssue
} from './generated'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'
import { NEGOTIATIONS_PATH } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { Translation } from 'locale'

export type Instrument = Collateral | CollateralBasket

export interface AxeTradeCondition {
  isAxed: boolean
  tradeCondition: TradeConditionFlag
}

export enum AxeActor {
  INITIATOR,
  RESPONDER
}

export interface OrderDetail {
  order: Order
  axeId: Nullable<string>
  collection: Nullable<Collection>
  containerCollection: Nullable<Collection>
  collateral: Nullable<Collateral>
  secondaryCollateral: Nullable<Collateral>
  secondaryCollateralBasket: Nullable<CollateralBasket>
  collateralBasket: Nullable<CollateralBasket>
  productSector: Nullable<ProductSector>
  product: Nullable<Product>
  orderFund: Nullable<TradingEntity>
  primeBroker: Nullable<PrimeBroker>
  referenceRate: Nullable<ReferenceRate>
  floatCrystallisationDays: Nullable<number>
  counterparty: Nullable<TradingEntityWithAlias>
  counterpartyIds: string[]
  axeTradeCondition: Nullable<AxeTradeCondition>
  investmentDecisionMaker: Nullable<User>
  executionDecisionMaker: Nullable<User>
  validationIssues: ValidationIssue[]
  availableEvents: OrderEvent[]
  secondaryInstrumentName: Nullable<string>
  clearingHouseName: Nullable<string>
  sponsoringBankName: Nullable<string>
  linkedTradeId: Nullable<string>
}

export type RecursivePartial<T> = {
  [K in keyof T]?: RecursivePartial<T[K]>
}

export type RecursiveNullable<T> = {
  [K in keyof T]: RecursiveNullable<T[K]> | null
}

export interface OrderSummary
  extends RecursivePartial<
    Pick<
      Order,
      | 'basketAllocation'
      | 'marketIdentifierCode'
      | 'collateralId'
      | 'status'
      | 'fundId'
      | 'term'
      | 'startDate'
      | 'endDate'
      | 'repoType'
      | 'createdBy'
      | 'createdAt'
      | 'evergreenNoticePeriod'
      | 'lastUpdatedBy'
      | 'referenceRate'
      | 'marketTradeDate'
      | 'notional'
      | 'accruedInterest'
      | 'startConsideration'
      | 'externalOrderId'
      | 'positionId'
      | 'callableType'
      | 'callablePeriodDays'
      | 'repoClassification'
      | 'noticePeriodDays'
      | 'currency'
      | 'market'
      | 'book'
      | 'strategy'
      | 'bundle'
      | 'blockTrade'
      | 'rfqType'
      | 'notes'
      | 'uploadRowIndex'
      | 'allowPartial'
      | 'preAgreed'
      | 'masterLegalAgreementType'
      | 'negotiationInstruction'
      | 'startConsiderationOverride'
      | 'secondaryCollateralId'
      | 'stockBorrowingLendingType'
      | 'secondaryBasketId'
      | 'servicingTrigger'
      | 'factor'
      | 'assetType'
      | 'rightOfExit'
      | 'rightOfSubstitution'
      | 'marketType'
      | 'haircutType'
      | 'haircut'
    >
  > {
  createdAtDate?: DateLike
  createdAtTime?: DateLike
  direction: Direction | SummaryRowDirection | null
  absoluteConsideration: number | null
}

export interface OrderDetailSummary {
  order: OrderSummary
  repoType: RepoType | null
  collection: RecursivePartial<Pick<Collection, 'status' | 'name' | 'type' | 'id'>>
  containerCollection: RecursivePartial<Pick<Collection, 'name' | 'id'>>
  collateral: RecursivePartial<Pick<Collateral, 'currency' | 'name' | 'issuer' | 'id' | 'isin' | 'cusip' | 'issue'>>
  collateralBasket: RecursivePartial<
    Pick<
      CollateralBasket,
      'name' | 'description' | 'dbvClass' | 'collateralBasketIsin' | 'collateralBasketCusip' | 'id' | 'productSectorId'
    >
  >
  productSector: RecursivePartial<Pick<ProductSector, 'name' | 'basketType' | 'basketCurrency'>>
  product: RecursivePartial<Pick<Product, 'name' | 'market'>>
  orderFund: RecursivePartial<Pick<TradingEntity, 'publishedName' | 'lei' | 'shortName' | 'id' | 'ficcShortName'>>
  primeBroker: RecursivePartial<Pick<PrimeBroker, 'name'>>
  referenceRate: RecursivePartial<Pick<ReferenceRate, 'name'>>
  floatCrystallisationDays: Nullable<number>
  investmentDecisionMaker: User | null
  executionDecisionMaker: User | null
  counterparty: RecursivePartial<Pick<TradingEntityWithAlias, 'publishedName' | 'alias'>>
  secondaryInstrumentName: string | null
  clearingHouseName: Nullable<string>
  sponsoringBankName: Nullable<string>
  linkedTradeId: Nullable<string>
}

export interface PreparedOrderDetail {
  orderDetail: OrderDetail
  preparedStagingOrder: PreparedStagingOrder
}

export interface ProductSectorAggregate {
  productSector: ProductSector | null
  product: Product | null
}

export enum SummaryRowDirection {
  MULTIPLE = 'MULTIPLE'
}

export interface SummaryRowNegotiation extends Negotiation {
  summaryDirection: Direction | SummaryRowDirection
}

export interface SummaryRowNegotiationDetail extends NegotiationDetail {
  negotiation: SummaryRowNegotiation
}

export interface NegotiationDetail {
  negotiation: Negotiation
  direction: Direction | null
  clearingHouse: ClearingHouse | null
  sponsor: Sponsor | null
  counterpartySponsor: Sponsor | null
  collection: Collection | null
  containerCollection: Collection | null
  collateral: Collateral | null
  collateralBasket: CollateralBasket | null
  productSector: ProductSector | null
  product: Product | null
  counterparty: TradingEntityWithAlias | null
  counterpartyFirm: Organisation | null
  orderFund: TradingEntityWithAlias | null
  orderFundFirm: Organisation | null
  referenceRate: ReferenceRate | null
  floatCrystallisationDays: Nullable<number>
  userWithLock: User | null
  axeTradeCondition: AxeTradeCondition
  ownOrder: boolean
  excludeFromBestPriceCalculations: boolean
  otwDuration: number | null
  validationIssues: ValidationIssue[]
  availableEvents: NegotiationEvent[]
  initiatorExecutionDecisionMaker: User | null
  initiatorInvestmentDecisionMaker: User | null
  responderExecutionDecisionMaker: User | null
  responderInvestmentDecisionMaker: User | null
  fundPrimarySalesCoverUser: User | null
  fundSecondarySalesCoverUser: User | null
  counterpartyPrimarySalesCoverUser: User | null
  counterpartySecondarySalesCoverUser: User | null
  notes: Notes | null
  secondaryInstrumentName: Nullable<string>
  usSponsoredRelationship: boolean
  linkedTradeId: Nullable<string>
}

export interface NegotiationHistoryDetail {
  negotiation: NegotiationHistory
  direction: Direction | null
  clearingHouse: ClearingHouse | null
  sponsor: Sponsor | null
  counterpartySponsor: Sponsor | null
  collection: Collection | null
  collateral: Collateral | null
  collateralBasket: CollateralBasket | null
  productSector: ProductSector | null
  product: Product | null
  counterparty: TradingEntityWithAlias | null
  counterpartyFirm: Organisation | null
  orderFund: TradingEntityWithAlias | null
  orderFundFirm: Organisation | null
  referenceRate: ReferenceRate | null
  userWithLock: User | null
  axeTradeCondition: AxeTradeCondition
  ownOrder: boolean
  excludeFromBestPriceCalculations: boolean
  otwDuration: number | null
  validationIssues: ValidationIssue[]
  availableEvents: NegotiationEvent[]
  initiatorExecutionDecisionMaker: User | null
  initiatorInvestmentDecisionMaker: User | null
  responderExecutionDecisionMaker: User | null
  responderInvestmentDecisionMaker: User | null
  fundPrimarySalesCoverUser: User | null
  fundSecondarySalesCoverUser: User | null
  counterpartyPrimarySalesCoverUser: User | null
  counterpartySecondarySalesCoverUser: User | null
  notes: Notes | null
  myEntityShortName: Nullable<string>
}

export interface NegotiationPackageDetail extends NegotiationDetail {
  [NEGOTIATIONS_PATH]: Record<string, NegotiationDetail>
  uuid: string
}

export interface InflightNegotiations {
  id: string
  collateral: Collateral
  direction?: Direction
  target?: number | null
  inflight?: number | null
  negotiationsByCounterparty: Record<string, NegotiationDetail[]>
}

export enum IsBestQuote {
  YES = 'YES',
  NO = 'NO',
  MATCHED = 'MATCHED'
}

export interface NegotiationDetailMeta {
  isBestQuote: IsBestQuote
  isCollectionSummary?: boolean
  isBestQuoteEligible?: boolean
}

export interface NegotiationDetailWithMeta extends NegotiationDetail {
  meta?: NegotiationDetailMeta
}

export interface ActivityDetail extends NegotiationDetail {
  respondedStatus: NegotiationStatus | null
}

export enum AuditStatus {
  COMPLETED = 'COMPLETED',
  ONGOING = 'ONGOING'
}

export interface AuditDetail extends Omit<NegotiationHistoryDetail, 'userWithLock' | 'otwDuration'> {
  auditId: string
  auditStatus: AuditStatus | null
}

export interface ServicingSideDetailsMeta {
  [NegotiationFieldMeta.COLLATERAL]: Collateral | null
  [NegotiationFieldMeta.FUND_ID]: TradingEntity | null
  [NegotiationFieldMeta.COUNTERPARTY_ID]: TradingEntity | null
  [NegotiationFieldMeta.START_DATE]: DateLike | null
  [NegotiationFieldMeta.END_DATE]: DateLike | null
  [NegotiationFieldMeta.TERM]: OrderTerm | null
  [NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER]: User | null
  [NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER]: User | null
  [NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]: User | null
  [NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER]: User | null
}

export interface DealDetail {
  deal: Deal
  dealInterestPeriod: Deal | null | undefined
  direction: Direction
  servicingSideDetails: ServicingSideDetails | null
  servicingSideDetailsMeta: ServicingSideDetailsMeta | null
  clearingHouse: ClearingHouse | null
  sponsor: Sponsor | null
  counterpartySponsor: Sponsor | null
  collection: Collection | null
  containerCollection: Collection | null
  collateral: Collateral | null
  collateralBasket: CollateralBasket | null
  productSector: ProductSector | null
  product: Product | null
  counterparty: TradingEntityWithAlias | null
  counterpartyFirm: Organisation | null
  orderFund: TradingEntityWithAlias | null
  orderFundFirm: Organisation | null
  referenceRate: ReferenceRate | null
  floatCrystallisationDays: Nullable<number>
  axeTradeCondition: AxeTradeCondition
  ownOrder: boolean
  isServicingInitiator: boolean | null
  userWithServicingLock: User | null
  initiatorExecutionDecisionMaker: User | null
  initiatorInvestmentDecisionMaker: User | null
  responderExecutionDecisionMaker: User | null
  responderInvestmentDecisionMaker: User | null
  validationIssues: ValidationIssue[]
  availableEvents: DealEvent[]
  isSelectedForRelatedDealTrades: boolean
  isSelectedForRelatedOpenRepoTrades: boolean
  primaryCoverUser: User | null
  parentCollateralBasket: CollateralBasket | null
  secondaryCoverUser: User | null
  isDealInterestPeriod: boolean
  isDealHistory: boolean
  notes: Notes | null
  secondaryInstrumentName: string | null
  negotiatedSecondaryInstrumentName: string | null
  collateralBasketName: string | null
  myEntityShortName: Nullable<string>
  ficcShortName: Nullable<string>
}

export type CommittingServicingEvent =
  | DealEvent.SERVICING_START_SUBSTITUTE
  | DealEvent.SERVICING_START_RERATE
  | DealEvent.SERVICING_START_TERMINATE
  | DealEvent.SERVICING_START_RETURN_RECALL
  | DealEvent.SERVICING_START_ROLL_IN_PLACE
  | DealEvent.SERVICING_START_TRADER_AMEND
  | DealEvent.SERVICING_START_TRADER_CANCEL
  | DealEvent.SERVICING_START_ADMIN_CANCEL
  | DealEvent.SERVICING_SEND
  | DealEvent.SERVICING_ACCEPT
  | DealEvent.SERVICING_REJECT
  | DealEvent.SERVICING_WITHDRAW

export interface TradingEntityAssignmentDetail {
  tradingEntityAssignment: FundTradingEntityAssignment
  tradingEntity: TradingEntity
  fieldValidationIssues: Record<string, ValidationIssue[]>
}

export interface CollateralAllocationDetail {
  collateralAllocation: CollateralAllocation
  collateral: Collateral
  fieldValidationIssues: Record<string, ValidationIssue[]>
}

export interface UserDetail {
  user: User
  organisation: Organisation | null
  availableEvents: CrudEventEnum[]
}

export interface PlatformDetail {
  platform: Platform
  availableEvents: CrudEventEnum[]
}

export interface OrganisationDetail {
  organisation: Organisation
  primeBrokers: PrimeBroker[]
  availableEvents: CrudEventEnum[]
}

export interface OrganisationMetaDetail {
  organisationMeta: OrganisationMeta
  ownerOrganisation: Organisation | null
  ownerTradingEntity: TradingEntity | null
  subjectOrganisation: Organisation | null
  subjectTradingEntity: TradingEntity | null
  product: Product | null
  productSector: ProductSector | null
  primaryCoverUser: User | null
  secondaryCoverUser: User | null
  availableEvents: CrudEventEnum[]
}

export interface TradingEntityWithAlias extends TradingEntity {
  alias?: string | null
}

export interface CounterpartyNameOrAliasDetail {
  tradingEntityMeta?: TradingEntityMeta
  publishedName: string
  counterpartyId: string
  alias?: string
}

export interface OrganisationAliasesDetail extends AliasDetail {
  availableEvents: CrudEventEnum[]
}

export interface AliasDetail {
  id: string
  index: number
  aliasTypeAndValue: AliasTypeAndValue
  ownerTradingEntity: TradingEntity
  subjectTradingEntity: TradingEntity
  metaId: string
}

export interface OrganisationPrimeBrokerDetail {
  id: TradingEntityMeta['id']
  ownerTradingEntity: TradingEntity
  subjectTradingEntity: TradingEntity
  primeBroker: PrimeBroker
  availableEvents: CrudEventEnum[]
}

export interface CounterpartyIdAndAlias {
  counterpartyId: string
  counterpartyAlias: string | null
}

export interface TradingEntityDetail {
  tradingEntity: TradingEntity
  organisation: Organisation | null
  tradingRelationships: TradingRelationship[]
  tradingEntityMeta: TradingEntityMeta[]
  availableEvents: CrudEventEnum[]
}

export interface PendingTradingEntityDetail {
  tradingEntity: TradingEntity
  organisation: Organisation | null
  validationIssues?: ValidationIssue[]
}

export interface TradingEntityMetaDetail {
  tradingEntityMeta: TradingEntityMeta
  ownerTradingEntity: TradingEntity | null
  subjectTradingEntity: TradingEntity | null
  subjectOrganisation: Organisation | null
  primaryCoverUser: User | null
  secondaryCoverUser: User | null
  primeBroker: PrimeBroker | null
  availableEvents: CrudEventEnum[]
}

export interface PendingTradingRelationshipDetail {
  tradingRelationship: TradingRelationship
  validationIssues: ValidationIssue[]
}

export interface ClearingHouseDetail {
  clearingHouse: ClearingHouse
  availableEvents: CrudEventEnum[]
}

export interface CollateralDetail {
  collateral: Collateral
  collateralPrice: CollateralPrice | null
  productSector: ProductSector | null
  product: Product | null
  index: Index | null
  availableEvents: CrudEventEnum[]
  lastUpdatedByUserName: string | null
}

export interface PendingCollateralDetail {
  collateral: Collateral
  productSector: ProductSector | null
  product: Product | null
  collateralPrice: CollateralPrice | null
  index?: Index | null
  validationIssues?: ValidationIssue[]
}

export interface CollateralBasketDetail {
  collateralBasket: CollateralBasket
  productSector: ProductSector | null
  product: Product | null
  availableEvents: CrudEventEnum[]
}

export interface HaircutEntryDetail {
  haircutEntry: HaircutEntry
  responderTradingEntity: TradingEntity | null
  responderOrganisation: Organisation | null
  initiatorTradingEntity: TradingEntityWithAlias | null
  initiatorOrganisation: Organisation | null
  product: Product | null
  productSector: ProductSector | null
  availableEvents: CrudEventEnum[]
}

export interface FatFingerDetail {
  fatFinger: FatFingerProductSectorLimit
  productSector: ProductSector | null
  product: Product | null
  availableEvents: CrudEventEnum[]
}

export interface HolidayDetail {
  holiday: Holiday
  date: DateLike
  availableEvents: CrudEventEnum[]
}

export interface CollectionDetail {
  collectionId: string
  collectionName: string
  subTotals: [number, number, number]
  totalItems: number
  minRfqEndTime?: DateLike
  minOtwEndTime?: DateLike
}

export interface TradingEntityClearingDetail {
  tradingEntityClearing: TradingEntityClearing
  clearingHouse: ClearingHouse | null
  sponsor: Sponsor | null
  productSectors: ProductSector[] | null
}

export interface TradingRelationshipDetail {
  tradingRelationship: TradingRelationship
  buyTradingEntity: Nullable<TradingEntity>
  sellTradingEntity: Nullable<TradingEntity>
  buySideOrg: Nullable<Organisation>
  sellSideOrg: Nullable<Organisation>
  organisationType: OrganisationType
}

export interface FatFingerUserLimitDetail {
  fatFingerUserLimit: FatFingerUserLimit
  productSector: Nullable<ProductSector>
  product: Nullable<Product>
  defaultFatFinger: FatFingerProductSectorLimit | undefined
}

export interface IndexDetail {
  index: Index
  availableEvents: CrudEventEnum[]
  lastUpdatedByUserName: string | null
}

export interface TradingControlLimitDetail {
  tradingControlLimit: TradingControlLimit
  organisation: Organisation | null
  availableEvents: CrudEventEnum[]
}

export type WorkflowDetail<TDetail> = TDetail & {
  availableEvents: ReferenceDataManagementEvent[]
}

export interface PreparedOrderClearingDetail {
  orderId: string
  clearingHouse: ClearingHouse | null
  sponsor: Sponsor | null
  swiftMessageId: string
}

export interface ProductDetail {
  product: Product
  availableEvents: CrudEventEnum[]
}

export interface ProductSectorMappingObject extends ProductSectorMappingDetail {
  productName: string | undefined
  productSectorName: string | undefined
}

export interface ProductSectorMappingDraftObject extends ProductSectorMappingDraftDetail {
  productName: string | undefined
  productSectorName: string | undefined
}

export interface ProductSectorDetail {
  productSector: ProductSector
  product: Product | null
  referenceRates: ReferenceRate[] | null
  availableEvents: CrudEventEnum[]
}

export interface FxRateDetail {
  fxRate: FxRate
  availableEvents: CrudEventEnum[]
}

export interface IssuerOrganisationDetail {
  issuerOrganisation: IssuerOrganisation
  ultimateParentName: string | null
  ultimateParentLEI: string | null
  ultimateParentPermId: string | null
}

export interface ReferenceDataEntityType<T> extends EntityWrapper {
  data: T
}

export interface ReferenceDataEntityTypeWithValidationIssues<T> extends EntityWrapper {
  data: T
  validationIssues: [ValidationIssue, ...ValidationIssue[]]
}

export type NegotiationOrAuditDetail =
  | SummaryRowNegotiationDetail
  | NegotiationDetail
  | NegotiationDetailWithMeta
  | NegotiationPackageDetail
  | AuditDetail

export const OpenRepoOrderTerms = [OrderTerm.O_O, OrderTerm.T_O, OrderTerm.S_O, OrderTerm.C_O, OrderTerm.CUSTOM_O]

export const FixedRepoOrderTerms = [
  OrderTerm.O_N,
  OrderTerm.O_W,
  OrderTerm.O_M,
  OrderTerm.T_N,
  OrderTerm.T_W,
  OrderTerm.T_M,
  OrderTerm.S_N,
  OrderTerm.S_W,
  OrderTerm.S_2W,
  OrderTerm.S_M,
  OrderTerm.S_3M,
  OrderTerm.S_6M,
  OrderTerm.S_Y,
  OrderTerm.C_N,
  OrderTerm.C_W,
  OrderTerm.C_2W,
  OrderTerm.C_M,
  OrderTerm.C_3M,
  OrderTerm.C_6M,
  OrderTerm.C_Y,
  OrderTerm.CUSTOM,
  OrderTerm.O_2W,
  OrderTerm.O_3W,
  OrderTerm.O_2M,
  OrderTerm.O_3M,
  OrderTerm.O_4M,
  OrderTerm.O_5M,
  OrderTerm.O_6M,
  OrderTerm.O_7M,
  OrderTerm.O_8M,
  OrderTerm.O_9M,
  OrderTerm.O_10M,
  OrderTerm.O_11M,
  OrderTerm.T_2W,
  OrderTerm.T_3W,
  OrderTerm.T_2M,
  OrderTerm.T_3M,
  OrderTerm.T_4M,
  OrderTerm.T_5M,
  OrderTerm.T_6M,
  OrderTerm.T_7M,
  OrderTerm.T_8M,
  OrderTerm.T_9M,
  OrderTerm.T_10M,
  OrderTerm.T_11M,
  OrderTerm.S_3W,
  OrderTerm.S_2M,
  OrderTerm.S_4M,
  OrderTerm.S_5M,
  OrderTerm.S_7M,
  OrderTerm.S_8M,
  OrderTerm.S_9M,
  OrderTerm.S_10M,
  OrderTerm.S_11M,
  OrderTerm.C_3W,
  OrderTerm.C_2M,
  OrderTerm.C_4M,
  OrderTerm.C_5M,
  OrderTerm.C_7M,
  OrderTerm.C_8M,
  OrderTerm.C_9M,
  OrderTerm.C_10M,
  OrderTerm.C_11M
]

export enum AxeSubscriptionIdentifier {
  AXE_TODAY_RESPONDER = 'AXE_TODAY_RESPONDER',
  AXE_TODAY_INITIATOR = 'AXE_TODAY_INITIATOR'
}

export enum NegotiationSubscriptionIdentifier {
  INITIATED = 'INITIATED',
  INITIATED_GRID_VIEW = 'INITIATED_GRID_VIEW',
  INCOMING = 'INCOMING',
  INITIATED_TERMINATED_NOTIFICATION = 'INITIATED_TERMINATED_NOTIFICATION',
  INCOMING_TERMINATED_NOTIFICATION = 'INCOMING_TERMINATED_NOTIFICATION'
}

export enum NegotiationHistorySubscriptionIdentifier {
  LATEST = 'LATEST', // Negotiation History screen (default, collapsed)
  HISTORY = 'HISTORY', // Negotiation History screen (expanded negotiation)
  HISTORICAL = 'HISTORICAL' // Historical screen
}

export enum DealSubscriptionIdentifier {
  BLOTTER = 'BLOTTER',
  OPEN_REPO = 'OPEN_REPO',
  STRUCTURED = 'STRUCTURED',
  MID_TRADE_EVENT = 'MID_TRADE_EVENT',
  DEAL_INTEREST_PERIOD = 'DEAL_INTEREST_PERIOD',
  DEAL_HISTORY = 'DEAL_HISTORY',
  ROLLS = 'ROLLS'
}

export enum DealHistorySubscriptionIdentifier {
  HISTORICAL_MID_TRADE_EVENTS = 'HISTORICAL_MID_TRADE_EVENTS'
}

export enum SpreadsheetUploadIdentifier {
  MTE_UPLOAD = 'MTE_UPLOAD'
}

export enum CollateralDetailsSubscriptionIdentifier {
  COLLATERAL_DETAILS = 'COLLATERAL_DETAILS'
}

export enum OrderSubscriptionIdentifier {
  STAGING = 'STAGING'
}

export const SubscriptionIdentifier = {
  ...AxeSubscriptionIdentifier,
  ...NegotiationSubscriptionIdentifier,
  ...NegotiationHistorySubscriptionIdentifier,
  ...DealSubscriptionIdentifier,
  ...DealHistorySubscriptionIdentifier,
  ...SpreadsheetUploadIdentifier,
  ...CollateralDetailsSubscriptionIdentifier,
  ...OrderSubscriptionIdentifier
}
export type SubscriptionIdentifier =
  | AxeSubscriptionIdentifier
  | NegotiationSubscriptionIdentifier
  | NegotiationHistorySubscriptionIdentifier
  | DealSubscriptionIdentifier
  | DealHistorySubscriptionIdentifier
  | SpreadsheetUploadIdentifier
  | CollateralDetailsSubscriptionIdentifier
  | OrderSubscriptionIdentifier

export const SUBSCRIPTION_PAGE_SIZE = 100
export const EXTENDED_SUBSCRIPTION_PAGE_SIZE = 200
export const NEGOTIATIONS_SUBSCRIPTION_PAGE_SIZE = 500
export const STAGING_SUBSCRIPTION_PAGE_SIZE = 500

export enum DefaultMarket {
  EU = 'EU',
  US = 'US',
  CUSTOM = 'CUSTOM'
}

export enum ProductNames {
  CORP = 'CORP'
}

export type LogConfig = ClientLogConfig['config']
export type SeverityTranslation = Extract<
  Translation,
  | Translation.TC_CLIENT_LOGSTREAM_SEVERITY_NONE
  | Translation.TC_CLIENT_LOGSTREAM_SEVERITY_INFO
  | Translation.TC_CLIENT_LOGSTREAM_SEVERITY_WARN
  | Translation.TC_CLIENT_LOGSTREAM_SEVERITY_ERROR
>
export const SeverityTranslation = {
  [Translation.TC_CLIENT_LOGSTREAM_SEVERITY_NONE]: Translation.TC_CLIENT_LOGSTREAM_SEVERITY_NONE,
  [Translation.TC_CLIENT_LOGSTREAM_SEVERITY_INFO]: Translation.TC_CLIENT_LOGSTREAM_SEVERITY_INFO,
  [Translation.TC_CLIENT_LOGSTREAM_SEVERITY_WARN]: Translation.TC_CLIENT_LOGSTREAM_SEVERITY_WARN,
  [Translation.TC_CLIENT_LOGSTREAM_SEVERITY_ERROR]: Translation.TC_CLIENT_LOGSTREAM_SEVERITY_ERROR
} as const

// used for precedence, be mindful if changing
export type LogSeverityPriority = { [K in LogSeverity]: number }
export const LogSeverityPriority: LogSeverityPriority = {
  [LogSeverity.INFO]: 1,
  [LogSeverity.WARN]: 2,
  [LogSeverity.ERROR]: 3
}

export enum Side {
  BUY,
  SELL
}

export enum SearchDealStatus {
  DONE = 'Done',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
  ASSIGNED = 'Assigned',
  UNASSIGNED = 'Unassigned',
  ALLOCATED = 'Allocated',
  UNALLOCATED = 'Unallocated',
  EXPIRED_ALLOCATION = 'Expired Allocation',
  CLEARING_PENDING = 'Clearing Pending',
  MID_TRADE_PENDING = 'Mid Trade Pending'
}

export type EventSpecificArgs<T> = Omit<T, 'eventType' | 'correlationId'>

export interface WithNestedLegs {
  nestedLegs?: NestedLegs | null
}

export type MaybeId = string | null | undefined

export const ServicingTypeNewDeal = 'NEW_DEAL'

export type WithDeal = {
  deal: Deal | null | undefined
}

export type WithCollateral = {
  collateral: Collateral | null | undefined
}

export interface CurrencyTradingLimitDetail extends TradingControlLimitUserUtilisationCurrency {
  currency: Currency
  limit: number
  availableGross: number
  availableNetBuy: number
  availableNetSell: number
}

export interface CollateralBasketTypePermissions {
  permittedToBePrimary: boolean
  basketPermittedToBeSblPrimary: boolean
  basketPermittedToBeSblSecondary: boolean
  requiresTradingEntityPermission: boolean
  settlementVenues: SettlementVenue[]
}

/**
 * map to apply the definition of specific business rules for each CollateralBasketType
 * copied from '../../../brokertec-quote-server/api/src/main/java/com/weareadaptive/brokertec/domain/CollateralBasketType.java'
 **/
export const collateralBasketTypePermissions: Record<CollateralBasketType, CollateralBasketTypePermissions> = {
  [CollateralBasketType.BONY]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: true,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Triparty]
  },
  [CollateralBasketType.CASH]: {
    permittedToBePrimary: false,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: true,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Triparty]
  },
  [CollateralBasketType.CLEAR_STREAM]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: true,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Triparty]
  },
  [CollateralBasketType.COLLATERAL]: {
    permittedToBePrimary: false,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: true,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Triparty]
  },
  [CollateralBasketType.DBV]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: false,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Bilateral, SettlementVenue.Sponsored, SettlementVenue.Direct]
  },
  [CollateralBasketType.EUROCLEAR]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: true,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Triparty]
  },
  [CollateralBasketType.GC_BESPOKE]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: true,
    basketPermittedToBeSblSecondary: false,
    requiresTradingEntityPermission: true,
    settlementVenues: [SettlementVenue.Bilateral, SettlementVenue.Sponsored, SettlementVenue.Direct]
  },
  [CollateralBasketType.GC_PLUS]: {
    permittedToBePrimary: true,
    basketPermittedToBeSblPrimary: false,
    basketPermittedToBeSblSecondary: false,
    requiresTradingEntityPermission: false,
    settlementVenues: [SettlementVenue.Sponsored, SettlementVenue.Direct]
  }
}

export enum DefaultHeading {
  AllInPrice = 'All in Price',
  Axe = 'Axe',
  BondPrice = 'Collateral Price',
  Book = 'Book',
  Bundle = 'Bundle',
  Callable = 'Callable',
  CallablePeriod = 'Callable Period',
  CollectionName = 'Collection Name',
  CollectionType = 'Collection Type',
  CollectionId = 'Collection ID',
  CounterpartyName = 'Counterparty Name',
  Counterparty = 'Counterparty',
  Csd = 'CSD',
  Direction = 'Direction',
  EndDate = 'End Date',
  EvergreenNoticePeriod = 'Evergreen Notice Period',
  ExternalId = 'Position Id',
  Fund = 'My Entity',
  IsinBui = 'ISIN/BUI',
  SecondaryIsinBui = 'Secondary ISIN/BUI',
  Note = 'Note',
  Notional = 'Notional',
  PreAgreed = 'Pre Agreed',
  PrimeBroker = 'Prime Broker',
  Rate = 'Rate',
  Fee = 'Fee',
  ReferenceRate = 'Reference Rate',
  RepoType = 'Repo Type',
  Roll = 'Roll',
  Sub = 'Sub',
  StartConsideration = 'Start Consideration',
  StartDate = 'Start Date',
  Strategy = 'Strategy',
  Term = 'Term',
  Unwind = 'Unwind',
  SettlementVenue = 'Settlement Type',
  ClearingHouse = 'CCP',
  Sponsor = 'Sponsor',

  EuGfidCode = 'EU GFID',
  UsGfidCode = 'US GFID',
  Eligible15a6 = '15a6 Eligible',
  EligibleCombinedRecFile = 'Combined EU/ US EOD File',
  CentralBank = 'Central Bank',
  NegotiationHistoryFile = 'Negotiation History File',
  OperatingCountry = 'Operating Country',

  // Incoming specific
  RfqId = 'RFQ ID',
  NegotiationId = 'Negotiation ID',
  HaircutType = 'Haircut Type',

  // Mid Trade Event specific
  TradeId = 'Trade ID',
  Action = 'Action',
  EffectiveDate = 'Effective Date',
  Haircut = 'Haircut',
  Response = 'Response',

  // Collateral specific
  TradingStatus = 'Trading',
  Platform = 'Platform',
  ShortId = 'Short ID',
  Isin = 'ISIN',
  Fisn = 'FISN',
  Cfi = 'CFI',
  Cusip = 'CUSIP',
  Currency = 'Currency',
  Country = 'Country',
  Sector = 'Sector',
  TradingType = 'Trading Type',
  CleanPrice = 'Clean Price',
  TicketSize = 'Ticket Size',
  Issuer = 'Issuer',
  FirstSettlementDate = 'First Settlement Date',
  AccrualDate = 'Accrual Date',
  FirstCouponDate = 'First Coupon Date',
  MaturityDate = 'Maturity Date',
  CouponType = 'Coupon Type',
  BasisConvention = 'Basis Convention',
  Coupon = 'Coupon',
  Frequency = 'Frequency',
  CollateralType = 'Collateral Type',
  Index = 'Index',
  Lag = 'Lag',
  BaseIndexValue = 'Base Index Value',

  // Trading Entity Specific
  Organisation = 'Organisation',
  PublishedName = 'Published Name',
  ShortName = 'Short Name',
  FiccShortName = 'FICC Short Name',
  Status = 'Status',
  Lei = 'LEI',
  Bic = 'BIC',
  TradingEntityType = 'Trading Type',

  // Trading Entity Product Specific
  OrganisationShortCode = 'Organisation Short Code',
  TradingEntityShortName = 'Trading Entity Short Name',
  Product = 'Product',
  Permission = 'Permission',

  // Trading Relationship Specific
  RelationshipAction = 'Action',
  BuyOrganisation = 'Buy Side Organisation',
  BuyTradingEntity = 'Buy Side Trading Entity',
  SellOrganisation = 'Sell Side Organisation',
  SellTradingEntity = 'Sell Side Trading Entity',
  UsSponsoredClearing = 'US Sponsored Clearing',
  UsSponsoredClearingDefault = 'US SC Default',
  MasterLegalAgreements = 'Master Legal Agreements',
  DefaultAgreement = 'Default Agreement',

  RightOfExit = 'RoE',
  RightOfSubstitution = 'RoS'
}
